import type { EOMInterfaceIdentifier } from '../eom'

/**
 * Extension that want to get options from the application could
 * extend this interface.  This is used when registering extensions (in the app) and
 * when the extension is initialized.
 *
 * @example In app
 * ``` ts
 *     const config = { url: new Url('myUrl'), myOption: true }
 *     Shell.addExtensions([config])
 * ```
 *
 *  @example In extension
 *   ```ts
 *   const messagingExtension = {
 *      /.../
 *      initialize: (config) => {
 *        if (config.myOption) {
 *          // do something here based on option passed by application
 *        }
 *      }
 *      /.../
 *  ```
 *   }
 *
 */
export interface ExtensionConfig {
  /**
   * Extension Id
   */
  readonly id: string
  /**
   * URL where the extension's script and css are stored
   */
  readonly url: URL

  /**
   * optional list of unguarded routes that does not require authentication
   */
  readonly unguardedRoutes?: readonly string[]

  /**
   * Whether the extension is served as an ES Module
   */
  readonly esModule?: boolean

  /**
   * list of route patterns that will load only that extension and it's dependencies
   */
  readonly standaloneRoutes?: readonly string[]

  /**
   * List of extension identifiers that are necessary to load prior this extension
   */
  readonly dependencies?: readonly string[]

  /**
   * List of conditions that need to pass before an extension is loaded
   */
  readonly prerequisites?: Prerequisites

  /**
   * List of all interfaces identifiers supported by the extension
   */
  readonly supportedInterfaces?: readonly InterfaceIdentifier[]
}

export type InterfaceIdentifier = EOMInterfaceIdentifier | string

export interface Prerequisites {
  readonly [PrerequisiteRuleTypes.ROLES]?: string
  readonly [PrerequisiteRuleTypes.PBX_FLAGS]?: string
  readonly [PrerequisiteRuleTypes.CONTACT_CENTER_FLAGS]?: string
  readonly [PrerequisiteRuleTypes.SHELL_FEATURE_FLAGS]?: string
  readonly [PrerequisiteRuleTypes.ACCOUNT_SETTINGS]?: string
}

export enum PrerequisiteRuleTypes {
  ROLES = 'roles',
  PBX_FLAGS = 'pbxFlags',
  CONTACT_CENTER_FLAGS = 'ccFlags',
  SHELL_FEATURE_FLAGS = 'shellFlags',
  ACCOUNT_SETTINGS = 'accountSettings',
}
